import React, { useEffect, useState, useCallback } from 'react';
import { scrollIntoViewWithOffset } from '../../../hooks/useScrollTarget';

export const BLOCK_ERROR_CLASS = 'blocking-error';

export const scrollToError = () => {
    try {
        scrollIntoViewWithOffset(`.${BLOCK_ERROR_CLASS}`, 100);
    } catch (e) {}
};

export const clearError = (alertId, alertBanners, setAlertBanners) => {
    if (!alertId || !alertBanners || !setAlertBanners) {
        return;
    }
    setAlertBanners(alertBanners.filter(banner => banner.key !== alertId));
};

export const getDefaultAlertBanners = (
    ctoError,
    orderItemLevelErrorMessages = [],
    alertBanners,
    clearError = () => {},
    localErrorID,
) => {
    const { qtyAlertID } = localErrorID || {};
    if (ctoError && ctoError.ctoErrorMessage) {
        const { ctoErrorMessage, checkoutButtonDisabled } = ctoError || {};
        const checkOutDisabled = checkoutButtonDisabled === 'true';
        const alertId = 'cto-error-alert';
        const closeBtnProps = !checkOutDisabled
            ? {
                  onClick: () => {
                      clearError(alertId);
                  },
              }
            : undefined;
        alertBanners.push({
            key: alertId,
            children: ctoErrorMessage,
            theme: 'secondary',
            closeBtnProps,
            className: checkOutDisabled ? BLOCK_ERROR_CLASS : '',
        });
    }

    alertBanners = alertBanners.concat(
        orderItemLevelErrorMessages.map((msg, index) => {
            const { message, type, checkoutButtonDisabled } = msg || {};
            const checkOutDisabled = checkoutButtonDisabled === 'true';
            //since we dont have helpful error types we have to rely on message content to identify qty errors
            //use the same alert ID for qty errors to avoid multiple alerts for same issue
            const isQtyError =
                message?.toLowerCase().includes('quantity') && message?.toLowerCase().includes('more than');
            const alertId = isQtyError && qtyAlertID ? qtyAlertID : `order-item-level-error-${index}`;
            const closeBtnProps = !checkOutDisabled
                ? {
                      onClick: () => {
                          clearError(alertId);
                      },
                      hasBoder: true,
                  }
                : undefined;
            return {
                key: alertId,
                children: message,
                theme: 'secondary',
                closeBtnProps: closeBtnProps,
                className: checkOutDisabled ? BLOCK_ERROR_CLASS : '',
            };
        }),
    );
    return alertBanners;
};

export default (ctoError, orderItemLevelErrorMessages, localErrorID) => {
    const [alertBanners, setAlertBanners] = useState([]);
    const [closedBanners, setClosedBanners] = useState([]);
    const addAlert = useCallback(
        (id, message, severity = 'warning') => {
            if (closedBanners.includes(id)) {
                //remove from closedBanners
                setClosedBanners(closedBanners.filter(bannerId => bannerId !== id));
            }
            //if alert is already open, do nothing
            if (alertBanners.find(banner => banner.key === id)) {
                return;
            }
            setAlertBanners([
                ...alertBanners,
                {
                    key: id,
                    children: message,
                    theme: 'secondary',
                    closeBtnProps: {
                        onClick: () => {
                            setClosedBanners([...closedBanners, id]);
                        },
                    },
                },
            ]);
        },
        [alertBanners, closedBanners],
    );
    const resetAlerts = () => {
        setAlertBanners([]);
        setClosedBanners([]);
    };
    const closeAlert = useCallback(
        id => {
            setClosedBanners([...closedBanners, id]);
        },
        [closedBanners],
    );

    //initialize alert banners with default values
    useEffect(() => {
        setAlertBanners(
            getDefaultAlertBanners(ctoError, orderItemLevelErrorMessages, [], id => closeAlert(id), localErrorID),
        );
    }, [ctoError, orderItemLevelErrorMessages]);
    //when closedBanners changes, update alertBanners
    useEffect(() => {
        if (!closedBanners.length) {
            return;
        }
        setAlertBanners(
            alertBanners.reduce((acc, banner) => {
                if (closedBanners.includes(banner.key)) {
                    return acc;
                }
                let { closeBtnProps, key } = banner;
                if (closeBtnProps?.onClick) {
                    //update close button props
                    closeBtnProps.onClick = () => {
                        closeAlert(key);
                    };
                }

                return [...acc, banner];
            }, []),
        );
    }, [closedBanners]);

    return {
        alertBanners: alertBanners.filter(({ key }) => !closedBanners.includes(key)),
        closedBanners,
        addAlertBanner: addAlert,
        closeAlertBanner: closeAlert,
        resetAlerts,
    };
};
